import {
  TestIntegrationErrorResponse,
  TestIntegrationRequest,
  TestIntegrationResponse,
  TestIntegrationSuccessResponse,
} from "@/@types/Integrations";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { IntegrationsService } from "@/services/integrations.service";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertCircle,
  Check,
  CheckCircle2,
  ChevronDown,
  Copy,
  ExternalLink,
  Loader2,
  Send,
  XCircle,
  Zap,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  filterPaymentMethodsForSubscriptionTest,
  isSubscriptionActiveStatus,
} from "@/utils/integrationSubscription";

export type IntegrationTestConfig = {
  requiredFields: ("title" | "postback_url" | "products" | "access_token")[];
  testFields: ("active_statuses" | "payment_methods")[];
  availableOptions: {
    active_statuses?: string[];
    payment_methods?: string[];
  };
};

export const integrationTestConfigs: Record<string, IntegrationTestConfig> = {
  webhook: {
    requiredFields: ["title", "postback_url", "products"],
    testFields: ["active_statuses", "payment_methods"],
    availableOptions: {},
  },
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  integrationKey: string;
  postbackUrl?: string | null;
  products: string[];
  availableStatuses: string[];
  availablePaymentMethods: string[];
};

type TestResult = {
  type: "success" | "error";
  response: TestIntegrationResponse;
  request: TestIntegrationRequest;
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3, ease: "easeOut" },
  },
};

const checkboxItemVariants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.2, ease: "easeOut" },
  },
};

export default function IntegrationTestModal({
  isOpen,
  onClose,
  integrationKey,
  postbackUrl,
  products,
  availableStatuses,
  availablePaymentMethods,
}: Props) {
  const { t } = useTranslation(["common", "integration", "sales"]);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [showPayloadDetails, setShowPayloadDetails] = useState(false);
  const [showResponseDetails, setShowResponseDetails] = useState(false);
  const [copiedPayload, setCopiedPayload] = useState(false);
  const [copiedResponse, setCopiedResponse] = useState(false);

  const config = integrationTestConfigs[integrationKey];

  const paymentMethodsForSelection = useMemo(() => {
    if (selectedStatus !== null && isSubscriptionActiveStatus(selectedStatus)) {
      return filterPaymentMethodsForSubscriptionTest(availablePaymentMethods);
    }

    return availablePaymentMethods;
  }, [selectedStatus, availablePaymentMethods]);

  const testMutation = useMutation({
    mutationFn: (data: TestIntegrationRequest) => IntegrationsService.testIntegration(data),
    onSuccess: (response, request) => {
      if (response.success) {
        setTestResult({ type: "success", response, request });
      } else {
        setTestResult({ type: "error", response, request });
      }
    },
    onError: (error: AxiosError<TestIntegrationErrorResponse>, request) => {
      const errorResponse = error.response?.data;
      if (errorResponse) {
        setTestResult({ type: "error", response: errorResponse, request });
      } else {
        toast({
          variant: "destructive",
          description: t("common:generic_error"),
        });
      }
    },
  });

  const handleStatusChange = (status: string) => {
    setSelectedStatus(status);

    const methodsForStatus = isSubscriptionActiveStatus(status)
      ? filterPaymentMethodsForSubscriptionTest(availablePaymentMethods)
      : availablePaymentMethods;

    if (
      selectedPaymentMethod !== null
      && !methodsForStatus.includes(selectedPaymentMethod)
    ) {
      setSelectedPaymentMethod(null);
    }
  };

  const handlePaymentMethodChange = (method: string) => {
    setSelectedPaymentMethod(method);
  };

  const handleCopy = async (data: unknown, setCopied: (v: boolean) => void) => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({ description: t("common:copied"), variant: "success" });
    } catch {
      toast({ description: t("common:errorCopying"), variant: "destructive" });
    }
  };

  const handleSendTest = () => {
    if (!selectedStatus) {
      toast({
        variant: "error",
        description: t("integration:test_webhook.select_event_type"),
      });
      return;
    }

    if (!selectedPaymentMethod) {
      toast({
        variant: "error",
        description: t("integration:test_webhook.select_payment_method"),
      });
      return;
    }

    const request: TestIntegrationRequest = {
      integration: integrationKey,
      postback_url: postbackUrl || undefined,
      active_statuses: [selectedStatus],
      payment_methods: [selectedPaymentMethod],
      products,
    };

    testMutation.mutate(request);
  };

  const handleTestAgain = () => {
    setTestResult(null);
    setShowPayloadDetails(false);
    setShowResponseDetails(false);
    setCopiedPayload(false);
    setCopiedResponse(false);
    testMutation.reset();
  };

  const handleClose = () => {
    if (testMutation.isPending) return;
    setSelectedStatus(null);
    setSelectedPaymentMethod(null);
    setTestResult(null);
    setShowPayloadDetails(false);
    setShowResponseDetails(false);
    setCopiedPayload(false);
    setCopiedResponse(false);
    testMutation.reset();
    onClose();
  };

  const handleCloseSuccess = () => {
    if (testResult?.type === "success") {
      toast({
        variant: "success",
        description: t("integration:test_webhook.test_sent_successfully"),
      });
    }
    handleClose();
  };

  if (!config) return null;

  const isValid = selectedStatus !== null && selectedPaymentMethod !== null;
  const hasPostbackUrl = postbackUrl && postbackUrl.trim().length > 0;
  const isSending = testMutation.isPending;

  const successData = testResult?.type === "success"
    ? (testResult.response as TestIntegrationSuccessResponse).data
    : null;

  const errorData = testResult?.type === "error"
    ? (testResult.response as TestIntegrationErrorResponse)
    : null;

  const getErrorMessage = (error: TestIntegrationErrorResponse) => {
    switch (error.error?.code) {
      case "HTTP_ERROR":
        return `${t("integration:test_webhook.error_http")} (HTTP ${error.error.http_status})`;
      case "CONNECTION_ERROR":
        return t("integration:test_webhook.error_connection");
      case "SERVICE_NOT_FOUND":
        return t("integration:test_webhook.error_service_not_found");
      case "TEST_NOT_SUPPORTED":
        return t("integration:test_webhook.error_not_supported");
      case "INVALID_PRODUCTS":
        return t("integration:test_webhook.error_invalid_products");
      default:
        return error.message;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-[540px] p-0 overflow-hidden border-zinc-800 max-h-[90vh] flex flex-col">
        <AnimatePresence mode="wait">
          {testResult ? (
            <motion.div
              key="result"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="flex flex-col max-h-[90vh]"
            >
              <div className="flex flex-col items-center justify-center py-8 sm:py-10 px-4 sm:px-6 shrink-0">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", damping: 15, stiffness: 300, delay: 0.1 }}
                  className="relative"
                >
                  {testResult.type === "success" ? (
                    <>
                      <div className="absolute inset-0 bg-primary/20 rounded-full blur-xl animate-pulse" />
                      <div className="relative flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-primary/30 to-primary/10 border border-primary/30">
                        <CheckCircle2 className="w-8 h-8 sm:w-10 sm:h-10 text-primary" />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="absolute inset-0 bg-red-500/20 rounded-full blur-xl animate-pulse" />
                      <div className="relative flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-red-500/30 to-red-500/10 border border-red-500/30">
                        <XCircle className="w-8 h-8 sm:w-10 sm:h-10 text-red-500" />
                      </div>
                    </>
                  )}
                </motion.div>
                <motion.h3
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className={cn(
                    "mt-4 sm:mt-6 text-lg sm:text-xl font-semibold text-center",
                    testResult.type === "success" ? "text-white" : "text-red-400"
                  )}
                >
                  {testResult.type === "success"
                    ? t("integration:test_webhook.test_sent_successfully")
                    : t("integration:test_webhook.test_failed")}
                </motion.h3>
                {hasPostbackUrl && (
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="mt-2 text-xs sm:text-sm text-muted-foreground text-center px-4 truncate max-w-full"
                  >
                    {postbackUrl}
                  </motion.p>
                )}
                {testResult.type === "error" && errorData && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="mt-3 flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 max-w-full"
                  >
                    <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                    <p className="text-xs sm:text-sm text-red-300">{getErrorMessage(errorData)}</p>
                  </motion.div>
                )}
              </div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="px-4 sm:px-6 pb-4 space-y-3 overflow-y-auto flex-1"
              >
                {successData && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-3">
                    <div className="bg-zinc-900/60 rounded-lg p-2.5 sm:p-3 border border-zinc-800">
                      <span className="text-[10px] sm:text-xs text-muted-foreground block mb-1">
                        HTTP Status
                      </span>
                      <span className={cn(
                        "text-sm sm:text-base font-semibold",
                        successData.http_status >= 200 && successData.http_status < 300
                          ? "text-primary"
                          : "text-yellow-400"
                      )}>
                        {successData.http_status}
                      </span>
                    </div>
                    <div className="bg-zinc-900/60 rounded-lg p-2.5 sm:p-3 border border-zinc-800">
                      <span className="text-[10px] sm:text-xs text-muted-foreground block mb-1">
                        {t("integration:test_webhook.sent_at")}
                      </span>
                      <span className="text-xs sm:text-sm text-zinc-300">
                        {new Date(successData.sent_at).toLocaleString()}
                      </span>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-2 sm:gap-3">
                  <div className="bg-zinc-900/60 rounded-lg p-2.5 sm:p-3 border border-zinc-800">
                    <span className="text-[10px] sm:text-xs text-muted-foreground block mb-1">
                      {t("integration:test_webhook.event_type")}
                    </span>
                    <div className="flex flex-wrap gap-1">
                      {testResult.request.active_statuses.map((status) => (
                        <span
                          key={status}
                          className="text-[10px] sm:text-xs px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/20"
                        >
                          {t(status)}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="bg-zinc-900/60 rounded-lg p-2.5 sm:p-3 border border-zinc-800">
                    <span className="text-[10px] sm:text-xs text-muted-foreground block mb-1">
                      {t("integration:test_webhook.payment_method")}
                    </span>
                    <div className="flex flex-wrap gap-1">
                      {testResult.request.payment_methods.map((method) => (
                        <span
                          key={method}
                          className="text-[10px] sm:text-xs px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20"
                        >
                          {t(method)}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Payload Enviado */}
                <div className="bg-zinc-900/60 rounded-lg border border-zinc-800 overflow-hidden">
                  <button
                    onClick={() => setShowPayloadDetails(!showPayloadDetails)}
                    className="w-full flex items-center justify-between p-2.5 sm:p-3 hover:bg-zinc-800/50 transition-colors"
                  >
                    <span className="text-xs sm:text-sm font-medium text-colorText flex items-center gap-2">
                      <Zap size={14} className="text-primary" />
                      {t("integration:test_webhook.payload_sent")}
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          const payload = successData?.payload_sent || errorData?.data?.payload_sent;
                          if (payload) handleCopy(payload, setCopiedPayload);
                        }}
                        className="h-7 w-7"
                      >
                        {copiedPayload ? (
                          <Check size={14} className="text-primary" />
                        ) : (
                          <Copy size={14} />
                        )}
                      </Button>
                      <ChevronDown
                        size={16}
                        className={cn(
                          "text-muted-foreground transition-transform duration-200",
                          showPayloadDetails && "rotate-180"
                        )}
                      />
                    </div>
                  </button>
                  <AnimatePresence>
                    {showPayloadDetails && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="border-t border-zinc-800">
                          <pre className="p-3 sm:p-4 text-[10px] sm:text-xs text-[#A9D342] overflow-x-auto max-h-40 sm:max-h-48 overflow-y-auto font-mono">
                            <code>
                              {JSON.stringify(
                                successData?.payload_sent || errorData?.data?.payload_sent,
                                null,
                                2
                              )}
                            </code>
                          </pre>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Resposta do Servidor (apenas em sucesso) */}
                {successData?.response && (
                  <div className="bg-zinc-900/60 rounded-lg border border-zinc-800 overflow-hidden">
                    <button
                      onClick={() => setShowResponseDetails(!showResponseDetails)}
                      className="w-full flex items-center justify-between p-2.5 sm:p-3 hover:bg-zinc-800/50 transition-colors"
                    >
                      <span className="text-xs sm:text-sm font-medium text-colorText flex items-center gap-2">
                        <ExternalLink size={14} className="text-blue-400" />
                        {t("integration:test_webhook.server_response")}
                      </span>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopy(successData.response, setCopiedResponse);
                          }}
                          className="h-7 w-7"
                        >
                          {copiedResponse ? (
                            <Check size={14} className="text-primary" />
                          ) : (
                            <Copy size={14} />
                          )}
                        </Button>
                        <ChevronDown
                          size={16}
                          className={cn(
                            "text-muted-foreground transition-transform duration-200",
                            showResponseDetails && "rotate-180"
                          )}
                        />
                      </div>
                    </button>
                    <AnimatePresence>
                      {showResponseDetails && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="border-t border-zinc-800">
                            <pre className="p-3 sm:p-4 text-[10px] sm:text-xs text-blue-300 overflow-x-auto max-h-40 sm:max-h-48 overflow-y-auto font-mono">
                              <code>{JSON.stringify(successData.response, null, 2)}</code>
                            </pre>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}


                {errorData?.error?.details && (
                  <div className="bg-red-500/5 rounded-lg border border-red-500/20 p-3">
                    <span className="text-[10px] sm:text-xs text-red-400 font-medium block mb-1">
                      {t("integration:test_webhook.error_details")}
                    </span>
                    <p className="text-xs text-red-300/80 font-mono break-all">
                      {errorData.error.details}
                    </p>
                  </div>
                )}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="px-4 sm:px-6 py-3 sm:py-4 border-t border-zinc-800 bg-zinc-900/30 shrink-0"
              >
                <div className="flex items-center gap-2 sm:gap-3">
                  <Button
                    variant="outline"
                    onClick={handleTestAgain}
                    className="flex-1 gap-2 border-zinc-700 hover:bg-zinc-800"
                  >
                    <Send size={16} />
                    {t("integration:test_webhook.test_again")}
                  </Button>
                  <Button onClick={handleCloseSuccess} className="flex-1 gap-2">
                    <Check size={16} />
                    {t("sales:export_modal.close")}
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          ) : (
            <motion.div
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col overflow-hidden"
            >
              <div className="relative px-4 sm:px-6 pt-4 sm:pt-6 pb-3 sm:pb-4 bg-gradient-to-b from-primary/5 to-transparent shrink-0">
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <DialogHeader className="space-y-0">
                    <div className="flex items-start gap-3 sm:gap-4">
                      <div className="relative shrink-0 max-sm:hidden">
                        <div className="absolute inset-0 bg-primary/30 rounded-xl blur-lg" />
                        <div className="relative flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20">
                          <Zap className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <DialogTitle className="text-base sm:text-lg font-semibold">
                          {t("integration:test_webhook.title")}
                        </DialogTitle>
                        <DialogDescription className="mt-0.5 sm:mt-1 text-xs sm:text-sm">
                          {t("integration:test_webhook.description")}
                        </DialogDescription>
                      </div>
                    </div>
                  </DialogHeader>
                </motion.div>
              </div>

              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="px-4 sm:px-6 pb-2 space-y-4 sm:space-y-5 overflow-y-auto flex-1"
              >
                {hasPostbackUrl && (
                  <motion.div variants={itemVariants} className="space-y-2">
                    <label className="flex items-center gap-2 text-xs sm:text-sm font-medium text-muted-foreground">
                      <ExternalLink className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      {t("integration:test_webhook.postback_url")}
                    </label>
                    <div className="group relative p-2.5 sm:p-3 bg-zinc-900/80 rounded-xl border border-zinc-800 hover:border-zinc-700 transition-colors">
                      <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity" />
                      <p className="relative text-xs sm:text-sm text-zinc-300 break-all font-mono">
                        {postbackUrl}
                      </p>
                    </div>
                  </motion.div>
                )}

                {config.testFields.includes("active_statuses") && (
                  <motion.div variants={itemVariants} className="space-y-2 sm:space-y-3">
                    <label className="flex items-center gap-2 text-xs sm:text-sm font-medium">
                      {t("integration:test_webhook.event_type")}
                    </label>
                    <motion.div
                      variants={containerVariants}
                      initial="hidden"
                      animate="visible"
                      className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 sm:gap-2 max-h-[120px] sm:max-h-[150px] overflow-y-auto pr-1 custom-scrollbar"
                    >
                      {availableStatuses.map((status, index) => {
                        const isSelected = selectedStatus === status;
                        return (
                          <motion.div
                            key={status}
                            variants={checkboxItemVariants}
                            custom={index}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => handleStatusChange(status)}
                            className={cn(
                              "flex items-center gap-2 sm:gap-2.5 p-2 sm:p-2.5 rounded-lg cursor-pointer transition-all duration-200",
                              "border border-transparent",
                              isSelected
                                ? "bg-primary/10 border-primary/30 shadow-sm shadow-primary/10"
                                : "bg-zinc-900/50 hover:bg-zinc-800/50 hover:border-zinc-700"
                            )}
                          >
                            <div
                              className={cn(
                                "w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors shrink-0",
                                isSelected
                                  ? "border-primary bg-primary"
                                  : "border-zinc-600"
                              )}
                            >
                              {isSelected && (
                                <div className="w-1.5 h-1.5 rounded-full bg-white" />
                              )}
                            </div>
                            <label
                              className={cn(
                                "text-xs sm:text-sm cursor-pointer transition-colors truncate",
                                isSelected ? "text-white font-medium" : "text-zinc-400"
                              )}
                            >
                              {t(status)}
                            </label>
                          </motion.div>
                        );
                      })}
                    </motion.div>
                  </motion.div>
                )}

                {config.testFields.includes("payment_methods") && (
                  <motion.div variants={itemVariants} className="space-y-2 sm:space-y-3">
                    <label className="flex items-center gap-2 text-xs sm:text-sm font-medium">
                      {t("integration:test_webhook.payment_method")}
                    </label>
                    <motion.div
                      variants={containerVariants}
                      initial="hidden"
                      animate="visible"
                      className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 sm:gap-2 max-h-[120px] sm:max-h-[150px] overflow-y-auto pr-1 custom-scrollbar"
                    >
                      {paymentMethodsForSelection.map((method, index) => {
                        const isSelected = selectedPaymentMethod === method;
                        return (
                          <motion.div
                            key={method}
                            variants={checkboxItemVariants}
                            custom={index}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => handlePaymentMethodChange(method)}
                            className={cn(
                              "flex items-center gap-2 sm:gap-2.5 p-2 sm:p-2.5 rounded-lg cursor-pointer transition-all duration-200",
                              "border border-transparent",
                              isSelected
                                ? "bg-primary/10 border-primary/30 shadow-sm shadow-primary/10"
                                : "bg-zinc-900/50 hover:bg-zinc-800/50 hover:border-zinc-700"
                            )}
                          >
                            <div
                              className={cn(
                                "w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors shrink-0",
                                isSelected
                                  ? "border-primary bg-primary"
                                  : "border-zinc-600"
                              )}
                            >
                              {isSelected && (
                                <div className="w-1.5 h-1.5 rounded-full bg-white" />
                              )}
                            </div>
                            <label
                              className={cn(
                                "text-xs sm:text-sm cursor-pointer transition-colors truncate",
                                isSelected ? "text-white font-medium" : "text-zinc-400"
                              )}
                            >
                              {t(method)}
                            </label>
                          </motion.div>
                        );
                      })}
                    </motion.div>
                  </motion.div>
                )}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="shrink-0"
              >
                <DialogFooter className="px-4 sm:px-6 py-3 sm:py-4 border-t border-zinc-800 bg-zinc-900/30">
                  <div className="flex items-center gap-2 sm:gap-3 w-full justify-end">
                    <Button
                      variant="ghost"
                      onClick={handleClose}
                      disabled={isSending}
                      className="text-muted-foreground hover:text-white text-xs sm:text-sm h-8 sm:h-9 px-3 sm:px-4"
                    >
                      {t("cancel")}
                    </Button>
                    <motion.div
                      whileHover={isValid && !isSending ? { scale: 1.02 } : {}}
                      whileTap={isValid && !isSending ? { scale: 0.98 } : {}}
                    >
                      <Button
                        onClick={handleSendTest}
                        disabled={isSending || !isValid}
                        className={cn(
                          "gap-1.5 sm:gap-2 min-w-[100px] sm:min-w-[140px] transition-all duration-300 text-xs sm:text-sm h-8 sm:h-9",
                          isValid && !isSending
                            ? "bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20"
                            : ""
                        )}
                      >
                        {isSending ? (
                          <>
                            <Loader2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 animate-spin" />
                            <span className="hidden sm:inline">{t("integration:test_webhook.sending")}</span>
                            <span className="sm:hidden">Enviando...</span>
                          </>
                        ) : (
                          <>
                            <Send className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                            {t("integration:test_webhook.send_test")}
                          </>
                        )}
                      </Button>
                    </motion.div>
                  </div>
                </DialogFooter>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}

export function useCanTestIntegration(
  integrationKey: string,
  formValues: {
    title?: string;
    postback_url?: string;
    products?: string[];
    access_token?: string;
  }
): boolean {
  const config = integrationTestConfigs[integrationKey];

  if (!config) return false;

  return config.requiredFields.every((field) => {
    const value = formValues[field];
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return Boolean(value && value.trim().length > 0);
  });
}

export function getMissingFieldsForTest(
  integrationKey: string,
  formValues: {
    title?: string;
    postback_url?: string;
    products?: string[];
    access_token?: string;
  }
): string[] {
  const config = integrationTestConfigs[integrationKey];

  if (!config) return [];

  return config.requiredFields.filter((field) => {
    const value = formValues[field];
    if (Array.isArray(value)) {
      return value.length === 0;
    }
    return !value || value.trim().length === 0;
  });
}
